// src/pages/attendance/hooks/useLocationState.js
import { useState, useEffect, useMemo, useCallback } from "react";
import { getAllowedArea, calculateAccurateDistance, locationTracker } from "../utils/locationService";

export const useLocationState = ({ hasApprovedWFHToday, isCheckingWFH, infinityCode, BASE_URL }) => {
  const [locationState, setLocationState] = useState({
    isLoadingArea: true, isLoadingUserLocation: true,
    allowedArea: null, userLocation: null,
    error: null, accuracy: null, accuracyLevel: "unknown",
    isTracking: false, lastUpdate: null,
  });

  // fetch work area
  useEffect(() => {
    if (isCheckingWFH || hasApprovedWFHToday) {
      if (hasApprovedWFHToday) setLocationState((p) => ({ ...p, isLoadingArea: false, isLoadingUserLocation: false }));
      return;
    }
    if (!infinityCode || !BASE_URL) {
      setLocationState((p) => ({ ...p, isLoadingArea: false, error: "Configuration error" }));
      return;
    }
    (async () => {
      try {
        const area = await getAllowedArea(infinityCode, BASE_URL);
        setLocationState((p) => ({ ...p, isLoadingArea: false, allowedArea: area, error: area.isFallback ? "Using default location" : null }));
      } catch (err) {
        setLocationState((p) => ({ ...p, isLoadingArea: false, error: err.message, allowedArea: null }));
      }
    })();
  }, [infinityCode, BASE_URL, hasApprovedWFHToday, isCheckingWFH]);

  // get user GPS
  useEffect(() => {
    if (isCheckingWFH || hasApprovedWFHToday) return;
    let mounted = true;
    (async () => {
      if (!navigator.geolocation) {
        setLocationState((p) => ({ ...p, isLoadingUserLocation: false, error: "Geolocation not supported" }));
        return;
      }
      setLocationState((p) => ({ ...p, isLoadingUserLocation: true, error: null }));
      try {
        let loc;
        try { loc = await locationTracker.getAveragedLocation(3); }
        catch { loc = await locationTracker.getAccurateLocation(); }
        if (!mounted) return;
        const level = loc.accuracy <= 10 ? "excellent" : loc.accuracy <= 20 ? "good" : loc.accuracy <= 50 ? "acceptable" : "poor";
        setLocationState((p) => ({ ...p, isLoadingUserLocation: false, userLocation: loc, accuracy: loc.accuracy, accuracyLevel: level, lastUpdate: new Date() }));
      } catch (err) {
        if (!mounted) return;
        setLocationState((p) => ({ ...p, isLoadingUserLocation: false, error: err.message }));
      }
    })();
    return () => { mounted = false; };
  }, [hasApprovedWFHToday, isCheckingWFH]);

  // continuous tracking
  useEffect(() => {
    if (!locationState.userLocation || !locationState.allowedArea) return;
    locationTracker.startContinuousTracking(
      ({ location, accuracyLevel }) => {
        setLocationState((p) => ({ ...p, userLocation: location, accuracy: location.accuracy, accuracyLevel, lastUpdate: new Date(), isTracking: true }));
      },
      () => {}
    );
    return () => locationTracker.stopContinuousTracking();
  }, [locationState.userLocation, locationState.allowedArea]);

  const distance = useMemo(() => {
    if (!locationState.userLocation || !locationState.allowedArea) return null;
    return calculateAccurateDistance(
      locationState.userLocation.lat, locationState.userLocation.lng,
      locationState.allowedArea.lat, locationState.allowedArea.lng
    );
  }, [locationState.userLocation, locationState.allowedArea]);

  const isOutOfZone = useMemo(() => {
    if (hasApprovedWFHToday) return false;
    if (locationState.isLoadingArea || locationState.isLoadingUserLocation) return true;
    if (!locationState.allowedArea || distance === null) return true;
    const buffer = Math.min(locationState.userLocation?.accuracy || 0, 50);
    return distance > locationState.allowedArea.radius + buffer;
  }, [distance, locationState, hasApprovedWFHToday]);

  const refreshLocation = useCallback(async () => {
    setLocationState((p) => ({ ...p, isLoadingUserLocation: true }));
    try {
      const loc = await locationTracker.getAveragedLocation(2);
      const level = loc.accuracy <= 10 ? "excellent" : loc.accuracy <= 20 ? "good" : loc.accuracy <= 50 ? "acceptable" : "poor";
      setLocationState((p) => ({ ...p, isLoadingUserLocation: false, userLocation: loc, accuracy: loc.accuracy, accuracyLevel: level, lastUpdate: new Date() }));
    } catch {
      setLocationState((p) => ({ ...p, isLoadingUserLocation: false, error: "Failed to refresh location" }));
    }
  }, []);

  return { locationState, distance, isOutOfZone, refreshLocation };
};